import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "@/components/vss/data";

function CountUp({ target, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1800;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return (
    <span ref={ref} className="tabular-nums brand-gradient">
      {n}
      {suffix}
    </span>
  );
}

export default function Stats() {
  return (
    <section
      id="stats"
      className="relative py-24 sm:py-32 border-y border-slate-200 bg-slate-50/60"
      data-testid="stats-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-12 items-end">
          <div className="col-span-12 lg:col-span-5">
            <div className="mb-3 font-mono-a text-[11px] uppercase tracking-[0.28em] text-teal-700">
              / 001 · Momentum
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-slate-900 font-bold tracking-tight leading-tight">
              Unprecedented momentum,
              <br />
              <span className="font-serif-a font-normal italic text-slate-500">
                delivery-first proof.
              </span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed max-w-[52ch]">
              With a relentless focus on delivery quality and supplier
              diversity, Visionary has achieved extraordinary expansion —
              supporting top-tier clients across regulated and hyperscale
              sectors from our Pune and Vadodara delivery centres.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-3xl overflow-hidden">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ delay: i * 0.08, duration: 0.7, ease: "easeOut" }}
              className="bg-white hover:bg-teal-50/40 transition p-6 sm:p-10"
              data-testid={`stat-${i}`}
            >
              <div className="font-mono-a text-[10px] uppercase tracking-[0.28em] text-teal-600 mb-4">
                0{i + 1}
              </div>
              <div className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">
                <CountUp target={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-4 text-slate-900 font-semibold text-sm sm:text-base">
                {s.label}
              </div>
              <div className="text-slate-500 text-xs sm:text-sm mt-1">
                {s.note}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
